import type {
  CharacterData,
  MyRosterEntry,
  PlayerMedia,
  ReleasedEntryResult,
  RosterData,
  RosterEntryData,
  SelectedEntryResult,
} from './types';
import type { PaginatedResponse } from '@/shared/types';
import { apiFetch } from '@/evennia_replacements/api';
import { readErrorDetail } from '@/lib/errors';
import { fetchAllPages } from '@/lib/pagination';

export async function fetchRosterEntry(id: RosterEntryData['id']): Promise<RosterEntryData> {
  const res = await apiFetch(`/api/roster/entries/${id}/`);
  if (!res.ok) {
    throw new Error('Failed to load roster entry');
  }
  return res.json();
}

/**
 * Make one of the character's images the one they wear (#3898).
 *
 * `tenureMediaId` is a `TenureMedia` pk — the `tenure_media_id` the sheet payload's
 * `looks` entries carry. The endpoint enforces that the media belongs to this entry
 * (and, for a non-staff caller, to their own tenure), so a wrong id is a 4xx rather
 * than a silent no-op. Returns nothing: 204 on success.
 */
export async function setEntryProfilePicture(
  entryId: RosterEntryData['id'],
  tenureMediaId: number
): Promise<void> {
  const res = await apiFetch(`/api/roster/entries/${entryId}/set_profile_picture/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenure_media_id: tenureMediaId }),
  });
  if (!res.ok) {
    await readErrorDetail(res, 'Failed to change the look.');
  }
}

export async function fetchMyRosterEntries(): Promise<MyRosterEntry[]> {
  const res = await apiFetch('/api/roster/entries/mine/');
  if (!res.ok) {
    throw new Error('Failed to load characters');
  }
  return res.json();
}

/**
 * Set (or clear, `entryId: null`) the account's durable server-side character
 * selection (#3412 state 2.5 substrate). Selection is NOT presence — this
 * triggers zero lifecycle/session/puppeting side effects server-side. Mirrors
 * the persona set-active endpoint's shape.
 */
export async function postSelectEntry(entryId: number | null): Promise<SelectedEntryResult> {
  const res = await apiFetch('/api/roster/entries/select/', {
    method: 'POST',
    body: JSON.stringify({ entry_id: entryId }),
  });
  if (!res.ok) {
    throw new Error('Could not switch to that character.');
  }
  return res.json();
}

/** The server's own refusal text, when the response body carries one (#3996). */
async function refusalMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body: unknown = await res.json();
    if (Array.isArray(body) && typeof body[0] === 'string') return body[0];
    if (body && typeof body === 'object') {
      const record = body as Record<string, unknown>;
      for (const key of ['detail', 'message']) {
        if (typeof record[key] === 'string') return record[key] as string;
      }
    }
  } catch {
    // no JSON body
  }
  return fallback;
}

async function postSlotAction<T>(entryId: number, path: string, fallback: string): Promise<T> {
  const res = await apiFetch(`/api/roster/entries/${entryId}/${path}/`, { method: 'POST' });
  if (!res.ok) {
    throw new Error(await refusalMessage(res, fallback));
  }
  return res.json();
}

/** Freeze an original character, freeing its slot (#3996). */
export function postFreezeEntry(entryId: number): Promise<MyRosterEntry> {
  return postSlotAction(entryId, 'freeze', 'Could not freeze that character.');
}

/** Thaw a frozen original character once its cooldown has passed (#3996). */
export function postThawEntry(entryId: number): Promise<MyRosterEntry> {
  return postSlotAction(entryId, 'thaw', 'Could not thaw that character.');
}

/** Give up a roster character: the tenure ends and it returns to Available (#3996). */
export function postGiveUpEntry(entryId: number): Promise<ReleasedEntryResult> {
  return postSlotAction(entryId, 'give-up', 'Could not give up that character.');
}

export async function fetchMyTenures(): Promise<{ id: number; display_name: string }[]> {
  const res = await apiFetch('/api/roster/tenures/mine/');
  if (!res.ok) {
    throw new Error('Failed to load tenures');
  }
  return res.json();
}

export async function fetchRosters(): Promise<RosterData[]> {
  const res = await apiFetch('/api/roster/rosters/');
  if (!res.ok) {
    throw new Error('Failed to load rosters');
  }
  return res.json();
}

export async function fetchPlayerMedia(): Promise<PlayerMedia[]> {
  // The endpoint is paginated (ADR-0138); the gallery grid shows the full
  // library, so walk every page.
  return fetchAllPages<PlayerMedia>('/api/roster/media/', 'Failed to load media');
}

export async function uploadPlayerMedia(form: FormData): Promise<PlayerMedia> {
  const res = await apiFetch('/api/roster/media/', { method: 'POST', body: form });
  if (!res.ok) {
    await readErrorDetail(res, 'Failed to upload media');
  }
  return res.json();
}

/** The roster entries read's filters; `realm` is a realm slug (#3725). */
export type RosterEntryFilters = Partial<Pick<CharacterData, 'name' | 'char_class' | 'gender'>> & {
  realm?: string;
};

export async function fetchRosterEntries(
  rosterId: RosterData['id'] | undefined,
  page = 1,
  filters: RosterEntryFilters = {}
): Promise<PaginatedResponse<RosterEntryData>> {
  const params = new URLSearchParams({ page: String(page) });
  // `roster` is optional server-side (world/roster/filters.py's RosterEntryFilterSet) —
  // omitting it searches across every public roster, which the character-card drawer's
  // identity resolution relies on (#2156): a persona name isn't scoped to a roster.
  if (rosterId !== undefined) params.set('roster', String(rosterId));
  if (filters.name) params.set('name', filters.name);
  if (filters.char_class) params.set('char_class', filters.char_class);
  if (filters.gender) params.set('gender', filters.gender);
  // Realm slug (#3725): entries whose sheet is from that realm, across every roster.
  if (filters.realm) params.set('realm', filters.realm);
  const res = await apiFetch(`/api/roster/entries/?${params.toString()}`);
  if (!res.ok) {
    throw new Error('Failed to load roster entries');
  }
  return res.json();
}
export async function postRosterApplication(
  id: RosterEntryData['id'],
  message: string
): Promise<void> {
  const res = await apiFetch(`/api/roster/entries/${id}/apply/`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
  if (!res.ok) {
    throw new Error(`Failed to send application for character ${id}`);
  }
}
